import i18next from 'i18next';
import { setImpersonationCookie } from './impersonation';
import { tokenManager, type AuthTokens } from './token-manager';
import { authConfig } from './auth-config';
import { getMcpReturnTo } from './mcp-return-to';
import { OAUTH_BRAND_SIGNUP_MARKER } from './oauth-signup-marker';
import { readAnalyticsChoice } from './analytics-choice';
import { resetProductAnalytics } from './product-analytics';

/**
 * OAuth Utility Functions
 * Handles Google OAuth flow for frontend authentication
 */

/**
 * Get user-friendly error message for OAuth error code
 * Uses i18next for internationalization
 */
export function getOAuthErrorMessage(errorCode: string): string {
  // Try to get translated message, fallback to error code if not found
  const translationKey = `auth.errors.oauth.${errorCode}`;
  const translated = i18next.t(translationKey);

  // If translation key doesn't exist, i18next returns the key itself
  // In that case, return the error code
  return translated !== translationKey ? translated : errorCode;
}

/**
 * Initiate Google OAuth flow for brand login
 * Redirects user to backend OAuth endpoint which then redirects to Google
 */
export function initiateGoogleAuth(returnTo?: string | null): void {
  const redirectUrl = `${window.location.origin}/`;
  const googleAuthUrl = new URL(`${authConfig.getApiUrl()}/auth/google/redirect`);
  googleAuthUrl.searchParams.set('redirect_url', redirectUrl);
  googleAuthUrl.searchParams.set('mode', 'brand_login');
  const continuation = getMcpReturnTo(returnTo);
  if (continuation) googleAuthUrl.searchParams.set('returnTo', continuation);

  // Redirect to backend OAuth endpoint
  window.location.href = googleAuthUrl.toString();
}

/**
 * Initiate the Google OAuth flow for a brand sign-up.
 *
 * With an invitation token, the invited brand accepts its invitation and comes
 * back to the invitation sign-up page. Without one, the brand signs up on its
 * own (the backend refuses it while the self sign-up is closed) and comes back
 * to `/signup`, where any refusal is explained.
 */
export function initiateGoogleBrandSignup(invitationToken?: string): void {
  const redirectUrl = invitationToken
    ? new URL('/', window.location.origin)
    : new URL('/signup', window.location.origin);

  if (invitationToken) {
    redirectUrl.searchParams.set('mode', 'signup');
    redirectUrl.searchParams.set('token', invitationToken);
  }
  redirectUrl.searchParams.set(
    'oauth',
    invitationToken ? OAUTH_BRAND_SIGNUP_MARKER.INVITATION : OAUTH_BRAND_SIGNUP_MARKER.SELF_SIGNUP
  );

  const googleAuthUrl = new URL(`${authConfig.getApiUrl()}/auth/google/redirect`);
  googleAuthUrl.searchParams.set('redirect_url', redirectUrl.toString());
  googleAuthUrl.searchParams.set('mode', 'brand_signup');
  const analyticsChoice = readAnalyticsChoice();
  if (analyticsChoice) googleAuthUrl.searchParams.set('analytics_consent', analyticsChoice);
  if (invitationToken) {
    googleAuthUrl.searchParams.set('invitation_token', invitationToken);
  }

  window.location.href = googleAuthUrl.toString();
}

/**
 * Extract OAuth tokens from URL parameters
 * Returns tokens if all required parameters are present, null otherwise
 */
export function extractTokensFromUrl(searchParams: URLSearchParams): AuthTokens | null {
  const accessToken = searchParams.get('accessToken');
  const refreshToken = searchParams.get('refreshToken');
  const type = searchParams.get('type');
  const expiresInStr = searchParams.get('expiresIn');

  // Check if all required parameters are present
  if (!accessToken || !refreshToken || !type || !expiresInStr) {
    return null;
  }

  // Validate token type
  if (type !== 'Bearer') {
    console.error('[OAUTH] Invalid token type:', type);
    return null;
  }

  // Parse and validate expiresIn
  const expiresIn = parseInt(expiresInStr, 10);
  if (isNaN(expiresIn) || expiresIn <= 0) {
    console.error('[OAUTH] Invalid expiresIn value:', expiresInStr);
    return null;
  }

  return {
    accessToken,
    refreshToken,
    type: 'Bearer',
    expiresIn,
  };
}

/**
 * Handle OAuth callback by processing tokens from URL
 * Stores tokens and returns success status
 */
export function handleOAuthCallback(searchParams: URLSearchParams): {
  success: boolean;
  error?: string;
} {
  try {
    // Check for error parameter first
    const error = searchParams.get('error');
    if (error) {
      console.log('[OAUTH] OAuth flow returned error:', error);
      return {
        success: false,
        error: getOAuthErrorMessage(error),
      };
    }

    // Extract tokens from URL
    const tokens = extractTokensFromUrl(searchParams);

    if (!tokens) {
      console.error('[OAUTH] Failed to extract tokens from URL parameters');
      return {
        success: false,
        error: 'Invalid authentication response. Please try again.',
      };
    }

    // Store tokens using token manager
    resetProductAnalytics();
    tokenManager.setTokens(tokens);

    // Detect impersonation session and set cookie
    if (searchParams.get('impersonation') === 'true') {
      setImpersonationCookie();
    }

    console.log('[OAUTH] Successfully processed OAuth callback and stored tokens');

    return { success: true };
  } catch (error) {
    console.error('[OAUTH] Error handling OAuth callback:', error);
    return {
      success: false,
      error: 'Failed to process authentication. Please try again.',
    };
  }
}

/**
 * Check if current URL contains OAuth callback parameters
 * Only returns true for successful OAuth responses with tokens
 * Error-only parameters are handled by the page itself
 */
export function isOAuthCallback(searchParams: URLSearchParams): boolean {
  return searchParams.has('accessToken');
}

/**
 * Clean OAuth parameters from URL without page reload
 * Removes sensitive token data from browser history
 */
export function cleanOAuthUrl(): void {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);

  // Remove OAuth-related parameters
  params.delete('accessToken');
  params.delete('refreshToken');
  params.delete('type');
  params.delete('expiresIn');
  params.delete('error');
  params.delete('impersonation');
  params.delete('oauth');

  // Update URL without reload
  const newUrl = params.toString() ? `${url.pathname}?${params.toString()}` : url.pathname;

  window.history.replaceState({}, '', newUrl);

  console.log('[OAUTH] Cleaned OAuth parameters from URL');
}
